import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import RolesTab from "@/pages/RolesTab";
import QuizTypesTab from "@/pages/QuizTypesTab";
import ResetRequestsTab from "@/pages/ResetRequestsTab";
import DepartmentsTab from "@/pages/DepartmentsTab";
import LocationsTab from "@/pages/LocationsTab";
import HodAssignmentsTab from "@/pages/HodAssignmentsTab";

export default function Masters() {
  const [depts, setDepts] = useState([]);
  const [subs, setSubs] = useState([]);
  const [cats, setCats] = useState([]);
  const [locs, setLocs] = useState([]);
  const [newSub, setNewSub] = useState({ name: "", parent_id: "" });
  const [newCat, setNewCat] = useState("");

  async function load() {
    const [d, s, c, l] = await Promise.all([
      api.get("/masters/departments"),
      api.get("/masters/sub-departments"),
      api.get("/masters/categories"),
      api.get("/masters/locations"),
    ]);
    setDepts(d.data); setSubs(s.data); setCats(c.data); setLocs(l.data);
  }
  useEffect(() => { load(); }, []);

  return (
    <div className="p-6 lg:p-10 space-y-6">
      <div>
        <div className="text-xs uppercase tracking-[0.2em] text-sky-600 font-bold">Master Data</div>
        <h1 className="font-display font-black text-3xl lg:text-4xl text-slate-900 mt-1">Masters & Locations</h1>
        <p className="text-slate-500 mt-1">Manage departments, sub-departments, categories, hospitals/locations, and multi-location HODs.</p>
      </div>

      <Tabs defaultValue="dept">
        <TabsList className="bg-slate-100 flex-wrap h-auto">
          <TabsTrigger value="dept" data-testid="masters-tab-dept">Departments</TabsTrigger>
          <TabsTrigger value="sub" data-testid="masters-tab-sub">Sub-Departments</TabsTrigger>
          <TabsTrigger value="cat" data-testid="masters-tab-cat">Categories</TabsTrigger>
          <TabsTrigger value="loc" data-testid="masters-tab-loc">Locations</TabsTrigger>
          <TabsTrigger value="hod" data-testid="masters-tab-hod">HOD Assignments</TabsTrigger>
          <TabsTrigger value="qtype" data-testid="masters-tab-qtype">Quiz Types</TabsTrigger>
          <TabsTrigger value="roles" data-testid="masters-tab-roles">Roles</TabsTrigger>
          <TabsTrigger value="resets" data-testid="masters-tab-resets">Reset Requests</TabsTrigger>
        </TabsList>

        <TabsContent value="dept" className="mt-4">
          <DepartmentsTab depts={depts} onChange={load}/>
        </TabsContent>

        <TabsContent value="sub" className="mt-4">
          <Card className="border-slate-200 rounded-2xl">
            <CardContent className="p-6 space-y-4">
              <div className="flex gap-2 items-end flex-wrap">
                <div className="flex-1 min-w-[180px]">
                  <Label className="text-xs uppercase tracking-wider text-slate-500">Name</Label>
                  <Input value={newSub.name} onChange={e=>setNewSub({...newSub, name: e.target.value})} placeholder="e.g. Cardiology"/>
                </div>
                <div className="flex-1 min-w-[180px]">
                  <Label className="text-xs uppercase tracking-wider text-slate-500">Parent Department</Label>
                  <Select value={newSub.parent_id || undefined} onValueChange={v=>setNewSub({...newSub, parent_id: v})}>
                    <SelectTrigger><SelectValue placeholder="Select…"/></SelectTrigger>
                    <SelectContent>{depts.map(d=> <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <Button onClick={async()=>{ if(!newSub.name||!newSub.parent_id) return; await api.post("/masters/sub-departments", newSub); setNewSub({name:"",parent_id:""}); load(); toast.success("Added"); }}
                  className="bg-sky-500 hover:bg-sky-600"><Plus className="h-4 w-4 mr-1"/>Add</Button>
              </div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
                {subs.map(s => (
                  <div key={s.id} className="flex items-center justify-between p-3 rounded-xl border border-slate-200 bg-white">
                    <div>
                      <div className="font-medium">{s.name}</div>
                      <div className="text-xs text-slate-400">{depts.find(d=>d.id===s.parent_id)?.name || "—"}</div>
                    </div>
                    <Button variant="ghost" size="sm" onClick={async()=>{ await api.delete(`/masters/sub-departments/${s.id}`); load(); }}>
                      <Trash2 className="h-4 w-4 text-red-500"/>
                    </Button>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="cat" className="mt-4">
          <SimpleList items={cats} addLabel="Add Category" placeholder="e.g. Paramedic" value={newCat}
            onChange={setNewCat}
            onAdd={async () => { if(!newCat) return; await api.post("/masters/categories", { name: newCat }); setNewCat(""); load(); toast.success("Added"); }}
            onDelete={async (id) => { await api.delete(`/masters/categories/${id}`); load(); }}
            testidPrefix="cat"
          />
        </TabsContent>

        <TabsContent value="loc" className="mt-4">
          <LocationsTab locs={locs} onChange={load}/>
        </TabsContent>

        <TabsContent value="hod" className="mt-4">
          <HodAssignmentsTab locs={locs}/>
        </TabsContent>

        <TabsContent value="roles" className="mt-4">
          <RolesTab />
        </TabsContent>

        <TabsContent value="qtype" className="mt-4">
          <QuizTypesTab />
        </TabsContent>

        <TabsContent value="resets" className="mt-4">
          <ResetRequestsTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function SimpleList({ items, addLabel, placeholder, value, onChange, onAdd, onDelete, testidPrefix }) {
  return (
    <Card className="border-slate-200 rounded-2xl">
      <CardContent className="p-6 space-y-4">
        <div className="flex gap-2">
          <Input placeholder={placeholder} value={value} onChange={e=>onChange(e.target.value)}
            data-testid={`${testidPrefix}-input`}/>
          <Button onClick={onAdd} className="bg-sky-500 hover:bg-sky-600" data-testid={`${testidPrefix}-add`}>
            <Plus className="h-4 w-4 mr-1"/>{addLabel}
          </Button>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
          {items.map(i => (
            <div key={i.id} className="flex items-center justify-between p-3 rounded-xl border border-slate-200 bg-white">
              <div className="font-medium">{i.name}</div>
              <Button variant="ghost" size="sm" onClick={()=>onDelete(i.id)}>
                <Trash2 className="h-4 w-4 text-red-500"/>
              </Button>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
